<template>
  <div class="d-flex flex-column">
    <div class="mt-n9" v-if="loading">
      <TableSkeleton />
    </div>
    <div
      class="d-flex justify-end align-end mt-n11 me-6 mb-2"
      v-if="!loading && totalFindings > 0"
    >
      <ColumnsSelector
        persistenceKey="routineView_routineFindings"
        :headers="headers"
        @toggleColumn="onToggleColumn"
        @setInitialColumns="setInitialColumns"
      />
    </div>
    <BulkActionsHeader
      v-if="showBulkActions"
      class="my-1"
      :selectedFindingsLength="selectedFindingsLength"
      :permissions="permissions"
      @bulkAddAssignees="handleBulkAddAssignees"
      @bulkDueDate="showBulkEditDueDateModal = true"
      @bulkDelete="handleDeleteClick(selectedFindings)"
    />
    <v-data-table
      v-model="selectedFindings"
      v-show="!loading"
      item-key="id"
      class="row-pointer"
      data-private
      :class="tableMarginClass"
      :show-select="hasPermissions"
      :headers="columns"
      :items="findings"
      :options.sync="options"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :server-items-length="totalFindings"
      :loading="loading"
      :footer-props="footerOptions"
      :no-data-text="$t('home.teams_activity.findings_tab_empty')"
      @click:row="openFindingDrawer"
      @update:options="trackOptions"
    >
      <template v-slot:[`item.situation`]="{ item }">
        <span
          v-snip="2"
          class="ma-0 pa-0 text-body-2"
          data-testid="routineFindings-text-situation"
          :title="item.situation"
        >
          {{ item.situation }}
        </span>
      </template>

      <template v-slot:[`item.dueDate`]="{ item }">
        <p class="ma-0 pa-0 body-2">
          {{
            item.dueDate
              ? formatDate(item.dueDate, 'MMM DD yyyy')
              : $t('common.no_due_date_label')
          }}
        </p>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <FindingStatus :status="item.status" />
      </template>

      <template v-slot:[`item.assignees`]="{ item }">
        <div class="text-no-wrap">
          <ChipAvatar small :account="item.assignees[0]" />

          <span
            v-if="item.assignees.length > 1"
            class="caption blue-grey--text text--darken-3"
          >
            +{{ item.assignees.length - 1 }}
          </span>
        </div>
      </template>
      <template v-slot:[`item.routine`]="{ item }">
        <router-link
          class="cursor-pointer text-decoration-none"
          :to="{
            name: 'routineExecutionDetail',
            params: { routineId: item.routine.id },
          }"
        >
          <span @click="handleExecutionLinkClick">
            {{ formatDate(item.routine.createdAt) }}
          </span>
        </router-link>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex justify-center">
          <Button
            color="blueGrey"
            data-testid="routineFindings-button-editFinding"
            icon="mdi-pencil"
            :disabled="
              !canEditFinding({
                routine: item.routine,
                routineDefinition,
                finding: item,
              })
            "
            :tooltip="$t('common.button.edit')"
            @click.stop="handleEditClick(item)"
          />

          <Button
            color="blueGrey"
            data-testid="routineFindings-button-deleteFinding"
            icon="mdi-trash-can-outline"
            :disabled="
              !canDeleteFinding({
                routine: item.routine,
                routineDefinition,
                finding: item,
              })
            "
            :tooltip="$t('common.button.delete')"
            @click.stop="handleDeleteClick([item])"
          />
        </div>
      </template>
    </v-data-table>

    <FindingDrawer
      v-if="showFindingDrawer"
      :visible="showFindingDrawer"
      :findingId="selectedFindingId"
      @close="onFindingDrawerClose({ update: true })"
      @deleted="onFindingDrawerClose({ update: true })"
    />
    <EditFindingDrawer
      v-if="openEditFindingDrawer"
      :visible="openEditFindingDrawer"
      :findingId="selectedFindingId"
      @close="onEditFindingDrawerClose"
    />
    <BulkDeleteConfirmationModal
      :show="showDeleteDialog"
      :findingsToDeleteIds="findingsToDeleteIds"
      :findingToDelete="findingToDelete"
      @cancel="showDeleteDialog = false"
      @success="deleteSuccess"
    />
    <BulkAddAssigneesModal
      v-if="showBulkAddAssigneesModal"
      :siteId="routineDefinitionSiteId"
      :selectedFindings="selectedFindings"
      @close="closeBulkModal"
      @success="bulkEditAssigneesSuccess"
    />

    <BulkEditDueDateModal
      v-if="showBulkEditDueDateModal"
      :siteId="routineDefinitionSiteId"
      :selectedFindings="selectedFindings"
      @close="closeBulkModal"
      @success="bulkEditDueDateSuccess"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { utils } from 'frontend-js-shared'
import _ from 'lodash'

import mixin from '@/mixins/mixin'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import findingPermissions from '@/mixins/findingPermissions.vue'

import FindingDrawer from '@/components/FindingDrawer/FindingDrawer.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'
import EditFindingDrawer from '@/components/FindingEditDrawer/FindingEditDrawer.vue'
import BulkDeleteConfirmationModal from '@/components/BulkDeleteConfirmationModal.vue'
import BulkActionsHeader from '@/components/BulkActionsHeader.vue'
import BulkEditDueDateModal from '@/components/BulkEditDueDateModal.vue'
import BulkAddAssigneesModal from '@/components/BulkAddAssigneesModal.vue'
import Button from '@/components/UI/Button'
import TableSkeleton from '@/components/TableSkeleton.vue'
import FindingStatus from '@/components/FindingDrawer/FindingStatus.vue'
import ColumnsSelector from '@/components/ColumnsSelector.vue'

const DEFAULT_SIZE = 20
const DEFAULT_SORT = 'createdAt'

const sortOrder = {
  ASCENDING: 'asc',
  DESCENDING: 'desc',
}

export default {
  name: 'RoutineFindings',

  mixins: [mixin, findingPermissions, trackerMixin],

  props: {
    routineDefinition: {
      type: Object,
      required: true,
    },
    findings: [],
    loading: Boolean,
    totalFindings: { type: Number, String },
  },

  components: {
    FindingDrawer,
    BulkDeleteConfirmationModal,
    EditFindingDrawer,
    BulkActionsHeader,
    BulkEditDueDateModal,
    BulkAddAssigneesModal,
    Button,
    ChipAvatar,
    TableSkeleton,
    FindingStatus,
    ColumnsSelector,
  },

  data() {
    return {
      firstLoading: true,
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      headers: [
        {
          text: this.$t('routines.finding_one'),
          align: 'start',
          sortable: true,
          value: 'situation',
          width: '260px',
          show: true,
        },
        {
          text: this.$t('common.due_date'),
          align: 'start',
          sortable: true,
          value: 'dueDate',
          width: '130px',
          show: true,
        },
        {
          text: this.$t('common.status'),
          align: 'start',
          sortable: true,
          value: 'status',
          width: 165,
          show: true,
        },
        {
          text: this.$t('common.assignee_other'),
          align: 'start',
          sortable: true,
          value: 'assignees',
          width: 234,
          show: true,
        },
        {
          text: this.$t('routines.execution_one'),
          align: 'start',
          sortable: true,
          value: 'routine',
          show: true,
        },
        {
          text: this.$t('common.action_other'),
          align: 'center',
          sortable: false,
          value: 'actions',
          width: 82,
          show: true,
        },
      ],
      sortBy: DEFAULT_SORT,
      sortDesc: true,
      currentPage: 1,
      selectedFindingId: null,
      showFindingDrawer: false,
      showDeleteDialog: false,
      findingToDelete: {},
      findingsToDeleteIds: [],
      selectedFindingRoutine: {},
      openEditFindingDrawer: false,
      selectedFindings: [],
      showBulkEditDueDateModal: false,
      showBulkAddAssigneesModal: false,
    }
  },

  computed: {
    ...mapGetters(['organizationId']),

    permissions() {
      if (_.isEmpty(this.routineDefinition)) return {}
      return this.getPermissions({
        routineDefinition: this.routineDefinition,
      })
    },

    options: {
      get() {
        return {
          page: this.currentPage,
          itemsPerPage: DEFAULT_SIZE,
          multiSort: false,
          mustSort: true,
        }
      },
      set(event) {
        this.$emit('getRoutineFindings', {
          page: event.page,
          itemsPerPage: event.itemsPerPage,
          sortBy: event.sortBy[0] ?? DEFAULT_SORT,
          sortDesc: event.sortDesc[0] ?? true,
        })
        this.selectedFindings = []
      },
    },

    selectedFindingsLength() {
      return this.selectedFindings.length ?? 0
    },

    hasPermissions() {
      return this.permissions.edit || this.permissions.delete
    },

    showBulkActions() {
      return this.selectedFindingsLength > 0 && this.hasPermissions
    },

    routineDefinitionSiteId() {
      return this.routineDefinition?.site?._id ?? ''
    },

    columns() {
      return this.headers.filter(h => h.show)
    },

    tableMarginClass() {
      return this.totalFindings > 0 ? 'mt-0' : 'mt-9'
    },
  },

  methods: {
    formatDate(date) {
      return utils.dates.formatDate(date, 'MMMM DD YYYY')
    },

    openFindingDrawer(item) {
      this.selectedFindingId = item.id
      this.showFindingDrawer = true
      this.trackRoutineDetailsTable({
        action: 'openFindingDrawer',
        id: item.id,
      })
    },

    onFindingDrawerClose() {
      this.selectedFindingId = null
      this.showFindingDrawer = false

      this.$emit('getRoutineFindings', this.options)
      this.selectedFindings = []
    },

    handleEditClick(finding) {
      this.selectedFindingId = finding.id
      this.selectedFindingRoutine = finding.routine
      this.openEditFindingDrawer = true
      this.trackRoutineDetailsTable({
        action: 'openEditFindingDrawer',
        id: finding.id,
      })
    },

    handleDeleteClick(finding) {
      if (finding.length === 1) this.findingToDelete = finding[0]
      this.findingsToDeleteIds = finding.map(element => element._id)
      this.showDeleteDialog = true
      this.trackRoutineDetailsTable({
        action: 'deleteFinding',
        numberOfFindingsToDelete: finding.length,
      })
    },

    onEditFindingDrawerClose() {
      this.selectedFindingId = null
      this.selectedFindingRoutine = {}
      this.openEditFindingDrawer = false
      this.$emit('getRoutineFindings', this.options)
    },

    deleteSuccess() {
      this.trackDeleteFinding({
        source: 'routineDetail',
        numberOfFindingsDeleted: !_.isEmpty(this.findingToDelete)
          ? 1
          : this.findingsToDeleteIds.length,
      })

      this.selectedFindings = []
      this.findingToDelete = {}
      this.findingsToDeleteIds = []
      this.showDeleteDialog = false
      this.$emit('getRoutineFindings', this.options)
    },

    closeBulkModal() {
      this.showBulkEditDueDateModal = false
      this.showBulkAddAssigneesModal = false
    },

    bulkEditAssigneesSuccess(data) {
      this.trackEditFinding({
        numberOfFindings: this.selectedFindingsLength,
        numberOfAssignees: data.totalNewAssignees,
      })
      this.selectedFindings = []
      this.showBulkEditDueDateModal = false
      this.showBulkAddAssigneesModal = false
      this.$emit('getRoutineFindings', this.options)
    },

    bulkEditDueDateSuccess() {
      this.trackEditFinding({
        numberOfFindings: this.selectedFindingsLength,
        action: 'newDueDate',
      })
      this.selectedFindings = []
      this.showBulkEditDueDateModal = false
      this.showBulkAddAssigneesModal = false
      this.$emit('getRoutineFindings', this.options)
    },

    onToggleColumn({ index, value }) {
      this.headers[index].show = value
      const name = this.headers[index].text

      this.trackRoutineDetailsTable({
        action: 'changeColumns',
        source: 'routineDetailsTable.findings',
        column: { [name]: value },
        routineDefinition: this.routineDefinition.id,
      })
    },

    trackOptions(options) {
      if (!this.firstLoading) {
        this.trackRoutineDetailsTable({
          action: 'getFindings',
          sortBy: options.sortBy,
          page: options.page,
          order: options?.sortDesc ? sortOrder.DESCENDING : sortOrder.ASCENDING,
        })
      }
      this.firstLoading = false
    },

    handleExecutionLinkClick(id) {
      this.trackRoutineDetailsTable({
        action: 'goToExecution',
        source: 'findingsTable',
        id: id,
      })
    },

    handleBulkAddAssignees() {
      this.trackRoutineDetailsTable({
        action: 'bulkAddAssignees',
        numberOfSelectedFindings: this.selectedFindingsLength,
      })
      this.showBulkAddAssigneesModal = true
    },

    setInitialColumns(selectedColumns) {
      this.headers = this.getInitialSelectedTableColumns(
        this.headers,
        selectedColumns
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.row-pointer:hover {
  cursor: pointer;
}
</style>
